import { ThemeSelectors } from "@at/design-system-foundation";
import { BREAKPOINTS } from "@at/window-utils/lib/responsive-utils/match-media.util";
import styled, { css, DefaultTheme } from "styled-components";

import { VehicleCheckStatus } from "../../../__generated__/graphql-types";
import { isUserInRedButtonTest } from "../../../utils/ab-test/ab-test.util";

const RedButtonTestInteractiveStyles = (theme: DefaultTheme) => css`
  color: ${ThemeSelectors.getDarkBlue(theme)};
  text-decoration: underline;
  text-underline-offset: 4px;
  text-decoration-thickness: 1px;
  &:hover {
    color: #eb0037;
  }
`;

export const VehicleCheckLabelContainer = styled.div`
  flex: auto;
  min-width: 0;
  padding: 0;
`;

export const VehicleCheckLabel = styled.span`
  flex: auto;
  flex-grow: 1;
  margin: 0;
`;

const VehicleCheckMessage = styled.p`
  color: ${({ theme }) => ThemeSelectors.getLegacyCopyLightColor(theme)};
`;

export const VehicleCheckStatusText = styled.div`
  ${({ theme }) => css`
    margin: 0;
    overflow: hidden;
    color: ${ThemeSelectors.getLegacyCopyLightColor(theme)};
    text-overflow: ellipsis;
    white-space: nowrap;

    & + ${VehicleCheckMessage} {
      margin-top: 2px;
    }
  `}
`;

export const VehicleCheckStatusValue = styled.span<{
  vehicleCheckStatus: VehicleCheckStatus;
  hasAdvisories: boolean;
}>`
  ${({ theme, vehicleCheckStatus, hasAdvisories }) => {
    const { FAILED, PASSED } = VehicleCheckStatus;

    if (vehicleCheckStatus === FAILED || hasAdvisories) {
      return isUserInRedButtonTest
        ? RedButtonTestInteractiveStyles(theme)
        : css`
            color: ${ThemeSelectors.getActionColor(theme)};
            &:hover {
              color: ${ThemeSelectors.getActionHoverColor(theme)};
            }
          `;
    }

    if (vehicleCheckStatus === PASSED) {
      return css`
        color: ${ThemeSelectors.getLegacyDarkGreen(theme)};
      `;
    }

    return css`
      color: ${ThemeSelectors.getLegacyCopyLightColor(theme)};
    `;
  }}
`;

export const VehicleCheckModal = styled.div`
  ${({ theme }) => css`
    fieldset {
      border: 1px solid ${ThemeSelectors.getDarkGrey(theme)};
      border-radius: ${ThemeSelectors.getGroupOneSpacing(theme)};
      margin: ${ThemeSelectors.getGroupFiveSpacing(theme)} 0;
      position: relative;

      ul {
        margin-top: ${ThemeSelectors.getGroupFourSpacing(theme)};
      }
    }

    legend {
      padding: ${ThemeSelectors.getGroupOneSpacing(theme)}
        ${ThemeSelectors.getGroupTwoSpacing(theme)};
      color: ${ThemeSelectors.getTitleColor(theme)};
    }
  `}
`;
export const ItemList = styled.div`
  ${({ theme }) => css`
        border-bottom: 1px solid ${ThemeSelectors.getDarkGrey(theme)};
        padding: 0px 0 ${ThemeSelectors.getGroupFourSpacing(theme)} 0;
        margin: ${ThemeSelectors.getGroupFourSpacing(theme)} 0;

        &:last-of-type {
            border: 0;
            padding: 0;
        }

        p {
            margin-top: calc(${ThemeSelectors.getGridUnit(theme)} * 2)
            color: ${ThemeSelectors.getTitleColor(theme)};
        }

        div {
            display: flex;
            justify-content: space-between;
            flex-flow: row nowrap;
            align-items: center;

            &:first-of-type {
                color: ${ThemeSelectors.getTitleColor(theme)};
            }

            span {
                width: 50%;
                display: flex;
                flex-direction: column;
                flex: 1;
                &:last-child {
                    text-align: right;
                }
            }
        }
    `}
`;

export const VehicleCheckModalHeader = styled.div`
  ${({ theme }) => css`
    display: block;
    position: relative;
    margin: 0 0 calc(${ThemeSelectors.getGridUnit(theme)} * 2);

    @media screen and (min-width: calc(${BREAKPOINTS.MEDIUM} * 1px)) {
      text-align: center;
    }
  `}
`;

export const VehicleCheckPartner = styled.div<{ $isFullCheck?: boolean }>`
  text-align: ${({ $isFullCheck }) => ($isFullCheck ? "left" : "center")};

  p {
    text-align: left;
  }
`;

export const VehicleCheckPartnerCTA = styled.div`
  display: flex;

  justify-content: center;
  margin: ${({ theme }) => `calc(${ThemeSelectors.getGridUnit(theme)} * 3) 0`};
  & > a {
    width: 100%;
  }
`;

export const VehicleCheckList = styled.ul`
  ${({ theme }) => {
    return css`
      margin: 0 0 calc(${ThemeSelectors.getGridUnit(theme)} * 3);

      li {
        position: relative;
      }
    `;
  }}
`;
export const VehicleCheckListText = styled.span`
  flex: 0 0 auto;
`;
interface VehicleCheckListIconProps {
  $itemStatus: string;
}

export const VehicleCheckListIcon = styled.span.attrs<VehicleCheckListIconProps>(
  ({ $itemStatus }) => ({
    title: $itemStatus,
  }),
)<VehicleCheckListIconProps>`
  display: inline-block;
  flex: 0 0 auto;
  width: 24px;
  height: 24px;

  svg {
    ${({ theme, $itemStatus }) => {
      let color = "";

      switch ($itemStatus) {
        case "Fail": {
          color = ThemeSelectors.getLegacyDarkYellow(theme);
          break;
        }
        case "Pass": {
          color = ThemeSelectors.getLegacyDarkGreen(theme);
          break;
        }
        default: {
          color = "currentColor";
          break;
        }
      }

      return css`
        fill: ${color};
      `;
    }}
  }
`;

interface VehicleCheckTooltipProps {
  $tooltipId: string;
}

export const VehicleCheckTooltip = styled.div.attrs<VehicleCheckTooltipProps>(
  ({ $tooltipId }) => ({
    id: $tooltipId,
    role: "tooltip",
  }),
)<VehicleCheckTooltipProps>`
  ${({ theme }) => {
    const colorBlue = ThemeSelectors.getDarkBlue(theme);
    const colorWhite = ThemeSelectors.getWhite(theme);
    const gridUnit = ThemeSelectors.getGridUnit(theme);

    return css`
      display: none;
      position: absolute;
      z-index: 1;
      left: 0;
      width: 100%;
      margin: auto;
      padding: ${gridUnit} calc(${gridUnit} * 1.5);
      border-radius: 4px;
      background: ${colorBlue};
      box-shadow: 0 calc(${gridUnit} * 1.5) calc(${gridUnit} * 1.5) 0
        rgba(0, 0, 0, 0.1);
      color: ${colorWhite};

      &::after {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: calc(${gridUnit} * 2);
        width: 0;
        height: 0;
        transform: translateY(-100%);
        border-right: ${gridUnit} solid transparent;
        border-bottom: calc(${gridUnit} * 0.75) solid ${colorBlue};
        border-left: ${gridUnit} solid transparent;
      }
    `;
  }}
`;

export const VehicleCheck = styled.div`
  ${({ theme }) => {
    const gridUnit = ThemeSelectors.getGridUnit(theme);

    return css`
      display: flex;
      position: relative;
      flex-flow: row nowrap;
      align-items: center;
      justify-content: space-between;
      padding: calc(${gridUnit} * 0.75) 0;
    `;
  }}
`;

interface VehicleCheckToggleProps {
  $label: string;
  $tooltipId: string;
}

export const VehicleCheckToggle = styled(
  VehicleCheck,
).attrs<VehicleCheckToggleProps>(({ $label, $tooltipId }) => ({
  tabIndex: 0,
  "aria-label": $label,
  "aria-describedby": $tooltipId,
}))<VehicleCheckToggleProps>`
  ${({ theme }) => css`
    ${isUserInRedButtonTest
      ? RedButtonTestInteractiveStyles(theme)
      : css`
          color: ${ThemeSelectors.getActionColor(theme)};
          &:hover {
            color: ${ThemeSelectors.getActionHoverColor(theme)};
          }
        `}

    &:hover {
      cursor: pointer;
    }

    &:focus {
      outline-color: currentColor;
    }

    &:hover,
    &:focus {
      & + ${VehicleCheckTooltip} {
        display: block;
      }
    }
  `}
`;
